import CalendarAgenda from './CalendarAgenda';
import CalendarNotification from './CalendarNotification';
import LoadingPanel from './LoadingPanel';
import ModuleSurfaceRenderer from './ModuleSurfaceRenderer';
import { focusCalendarSurface } from '../actions/focusCalendarSurface';
import { default as surfaceConfig } from '../configs/OwaCalendarSurfaceConfig';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { lazySetAndNavigateFileImport } from 'owa-calendar-addcalendar';
import { DropHint } from 'owa-calendar-drophint';
import { CalendarSurface, CalendarSurfaceHandle } from 'owa-calendar-surface-core';
import { getSurfaceStore } from 'owa-calendar-surface-surface-store';
import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { LocalFilesDragData } from 'owa-dnd/lib/utils/LocalFilesDragData';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import * as React from 'react';
import {
    canDropCalendarItem,
    getIsDropHintVisible,
    hideDropHint,
    showDropHint,
} from 'owa-calendar-surface-view-store';

const styles = require('./CalendarModuleSurface.scss');

export default observer(function CalendarModuleSurface() {
    const emptyDropViewState = createDropViewState();

    const surfaceRef = React.useRef<CalendarSurfaceHandle>();
    const surfaceStore = getSurfaceStore();

    useOrchestrator(
        focusCalendarSurface,
        () => {
            surfaceRef.current?.focusSurfaceRoot();
        },
        []
    );

    return (
        <>
            <Droppable
                classNames={styles.moduleSurface}
                canDrop={canDrop}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                dropViewState={emptyDropViewState}
                shouldUseCopyAsDropEffect={true}
                shouldIgnoreTransientOnDragLeave={true}>
                <LoadingPanel>
                    {getIsDropHintVisible() && <DropHint onDrop={onFileDrop} />}
                    <CalendarSurface config={surfaceConfig} store={surfaceStore} ref={surfaceRef} />
                </LoadingPanel>
                <CalendarAgenda />
                <CalendarNotification />
            </Droppable>
            <ModuleSurfaceRenderer surfaceHandle={surfaceRef.current} />
        </>
    );
});

function canDrop(dragInfo: DragData): boolean {
    return dragInfo.itemType === DraggableItemTypes.LocalFile || canDropCalendarItem();
}

function onDrop(dragInfo: DragData) {
    hideDropHint();
}

function onDragOver(dragInfo: DragData, pageX: number, pageY: number) {
    if (dragInfo.itemType === DraggableItemTypes.LocalFile) {
        showDropHint();
    }
}

function onDragLeave(dragInfo: DragData) {
    hideDropHint();
}

function onFileDrop(dragInfo: DragData, pageX: number, pageY: number) {
    if (dragInfo.itemType === DraggableItemTypes.LocalFile) {
        const filesDragData = dragInfo as LocalFilesDragData;
        const file = filesDragData.files[0];
        lazySetAndNavigateFileImport.importAndExecute(file);
        logUsage('DragDropFileOnCalendar');
    }
}
